import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { computeScore, SCORED_QUESTIONS, TOTAL_QUESTIONS, type ScoredAnswers, type ScoreResult } from './scoring';
import { useScreenInit } from '../useScreenInit.js';
interface ScoreContextType {
  answers: ScoredAnswers;
  setAnswer: (questionId: string, value: number) => void;
  answeredCount: number;
  isComplete: boolean;
  result: ScoreResult | null;
  isDiagnosticOpen: boolean;
  openDiagnostic: () => void;
  closeDiagnostic: () => void;
}
const ScoreContext = createContext<ScoreContextType | undefined>(undefined);
const ANSWERS_STORAGE_KEY = 'authorLeverage:answers';
const SCORE_STORAGE_KEY = 'bas:lastScore';
const DEMO_SEED = false;
function getSavedAnswers(): ScoredAnswers {
  if (DEMO_SEED) {
    return {
      'q2.intent.handRaiserContext': 1
    };
  }
  if (typeof window === 'undefined') return {};
  try {
    const storedAnswers = window.sessionStorage.getItem(ANSWERS_STORAGE_KEY);
    return storedAnswers ? JSON.parse(storedAnswers) as ScoredAnswers : {};
  } catch {
    return {};
  }
}
export function ScoreProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const screenInit = useScreenInit();
  const [answers, setAnswers] = useState<ScoredAnswers>(getSavedAnswers);
  const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(() => screenInit.isDiagnosticOpen === true);
  const setAnswer = useCallback((questionId: string, value: number) => {
    setAnswers(currentAnswers => ({
      ...currentAnswers,
      [questionId]: value
    }));
  }, []);
  const answeredCount = SCORED_QUESTIONS.filter(question => answers[question.id] !== undefined).length;
  const isComplete = answeredCount === TOTAL_QUESTIONS;
  const result = isComplete ? computeScore(answers) : null;
  useEffect(() => {
    try {
      window.sessionStorage.setItem(ANSWERS_STORAGE_KEY, JSON.stringify(answers));
    } catch {
      // Progress remains available for the current page session if storage is blocked.
    }
  }, [answers]);
  useEffect(() => {
    if (!result) return;
    try {
      window.sessionStorage.setItem(SCORE_STORAGE_KEY, JSON.stringify({
        ...result,
        completedAt: new Date().toISOString()
      }));
    } catch {
      // The completed result remains available in the current session.
    }
  }, [result]);
  const openDiagnostic = useCallback(() => {
    setIsDiagnosticOpen(true);
  }, []);
  const closeDiagnostic = useCallback(() => {
    setIsDiagnosticOpen(false);
  }, []);
  return <ScoreContext.Provider value={{
    answers,
    setAnswer,
    answeredCount,
    isComplete,
    result,
    isDiagnosticOpen,
    openDiagnostic,
    closeDiagnostic
  }} data-id="element-470">
      {children}
    </ScoreContext.Provider>;
}
export function useScore() {
  const context = useContext(ScoreContext);
  if (context === undefined) {
    throw new Error('useScore must be used within a ScoreProvider');
  }
  return context;
}